// =============================================================================
// CreditNoteDetailPage — open one credit note, apply / void / download PDF
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { PageScaffold } from '../../../components/PageScaffold';
import { cn } from '../../../lib/cn';

interface CreditNoteDetail {
  id: string;
  number: string;
  status: 'DRAFT' | 'ISSUED' | 'APPLIED' | 'VOID';
  reason: string;
  vatExempt: boolean;
  subtotal: string;
  vatTotal: string;
  total: string;
  appliedTotal: string;
  remainingTotal: string;
  notes: string | null;
  issuedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
  customer: {
    id: string;
    name: string;
    companyName: string | null;
    type: 'individual' | 'company';
    customerCode: string | null;
    vatNumber: string | null;
    brn: string | null;
    contactPerson: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    city: string | null;
    country: string | null;
  };
  invoice: { id: string; number: string; order: { total: string; balanceDue: string } } | null;
  lines: Array<{
    id: string;
    description: string;
    qty: number;
    unitPrice: string;
    discount: string;
    vatRate: string;
    lineTotal: string;
  }>;
  journalEntry: {
    id: string;
    number: string;
    date: string;
    lines: Array<{
      id: string;
      debit: string;
      credit: string;
      narration: string | null;
      account: { code: string; name: string };
    }>;
  } | null;
  applications: Array<{
    id: string;
    amount: string;
    appliedAt: string;
    invoice: { number: string };
    appliedBy: { name: string };
  }>;
  createdBy: { name: string };
}

function statusTone(s: string): 'positive' | 'warning' | 'negative' | 'neutral' {
  switch (s) {
    case 'APPLIED':
      return 'positive';
    case 'ISSUED':
      return 'warning';
    case 'VOID':
      return 'negative';
    default:
      return 'neutral';
  }
}

function reasonLabel(r: string): string {
  return r
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

export function CreditNoteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [showApply, setShowApply] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  const detail = useQuery({
    queryKey: ['credit-note', id],
    queryFn: () => apiGet<CreditNoteDetail>(`/credit-notes/${id}`),
    enabled: !!id,
  });

  const downloadPdf = (mode: 'download' | 'inline'): void => {
    if (!detail.data) return;
    const cn = detail.data;
    void (async () => {
      try {
        const res = await api.get(`/credit-notes/${cn.id}/pdf`, {
          params: { mode },
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (mode === 'inline') {
          const w = window.open(url, '_blank');
          if (w) setTimeout(() => w.print(), 500);
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${cn.number}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  if (detail.isLoading) {
    return (
      <PageScaffold title="Credit note" back={{ to: '/credits', label: 'Credit notes' }}>
        <div className="text-ink-3">Loading…</div>
      </PageScaffold>
    );
  }
  if (!detail.data) {
    return (
      <PageScaffold title="Credit note" back={{ to: '/credits', label: 'Credit notes' }}>
        <div className="text-ink-3">Credit note not found.</div>
      </PageScaffold>
    );
  }
  const cn = detail.data;
  const isCompany = cn.customer.type === 'company';
  const displayName = isCompany ? (cn.customer.companyName ?? cn.customer.name) : cn.customer.name;
  const totalNum = Number(cn.total) || 1;
  const appliedNum = Number(cn.appliedTotal);
  const remainingNum = Number(cn.remainingTotal);
  const pct = Math.min(100, Math.round((appliedNum / totalNum) * 100));

  const HEADER_ACTIONS = (
    <>
      <Button variant="secondary" onClick={() => downloadPdf('download')}>
        Download PDF
      </Button>
      <Button variant="secondary" onClick={() => downloadPdf('inline')}>
        Print
      </Button>
      <EmailDocButton
        endpoint={`/credit-notes/${cn.id}/email`}
        defaultEmail={cn.customer.email}
        title="Email credit note"
        subtitle="Send the credit note PDF to the customer."
      />
      {cn.status === 'ISSUED' && remainingNum > 0 && (
        <Button onClick={() => setShowApply(true)}>Apply to invoice</Button>
      )}
      {cn.status === 'ISSUED' && remainingNum > 0 && (
        <Button
          variant="secondary"
          onClick={() => setShowRefund(true)}
          title="Pay the credit back in cash/transfer"
        >
          Refund cash
        </Button>
      )}
      {cn.status === 'ISSUED' && appliedNum === 0 && (
        <Button variant="ghost" onClick={() => setShowVoid(true)}>
          Void
        </Button>
      )}
    </>
  );

  return (
    <PageScaffold
      title={cn.number}
      back={{ to: '/credits', label: 'Credit notes' }}
      actions={HEADER_ACTIONS}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill tone={statusTone(cn.status)}>{cn.status.toLowerCase()}</StatusPill>
        <StatusPill tone="neutral">{reasonLabel(cn.reason)}</StatusPill>
        {cn.vatExempt && <StatusPill tone="info">VAT-exempt</StatusPill>}
        {isCompany && <StatusPill tone="accent">B2B</StatusPill>}
        <span className="text-xs text-ink-3">
          Issued {new Date(cn.issuedAt ?? cn.createdAt).toLocaleString('en-MU')}
          {' · by '}
          {cn.createdBy.name}
        </span>
      </div>

      {cn.status === 'VOID' && (
        <Card bodyClassName="px-6 py-4 bg-negative-soft border-negative/30">
          <div className="text-sm text-negative font-medium">
            This credit note was voided on{' '}
            {new Date(cn.voidedAt ?? cn.createdAt).toLocaleString('en-MU')}.
          </div>
          {cn.voidReason && <div className="text-xs text-ink-2 mt-1">Reason: {cn.voidReason}</div>}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Lines */}
          <Card title="Lines" bodyClassName="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th>
                  <th className="px-3 py-2 font-medium text-right">Unit</th>
                  <th className="px-3 py-2 font-medium text-right">Discount</th>
                  <th className="px-6 py-2 font-medium text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {cn.lines.map((l) => (
                  <tr key={l.id} className="border-b border-rule-2">
                    <td className="px-6 py-3 text-ink">{l.description}</td>
                    <td className="px-3 py-3 text-right kc-mono">{l.qty}</td>
                    <td className="px-3 py-3 text-right">
                      <Money value={l.unitPrice} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Money value={l.discount} muted={l.discount === '0'} />
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={l.lineTotal} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="text-sm">
                  <td colSpan={3} />
                  <td className="px-3 py-2 text-right text-ink-3">Subtotal</td>
                  <td className="px-6 py-2 text-right">
                    <Money value={cn.subtotal} />
                  </td>
                </tr>
                <tr className="text-sm">
                  <td colSpan={3} />
                  <td className="px-3 py-2 text-right text-ink-3">
                    {cn.vatExempt ? 'VAT (exempt)' : 'VAT (reversed)'}
                  </td>
                  <td className="px-6 py-2 text-right">
                    {cn.vatExempt ? '—' : <Money value={cn.vatTotal} />}
                  </td>
                </tr>
                <tr className="text-sm border-t border-rule">
                  <td colSpan={3} />
                  <td className="px-3 py-3 text-right font-medium text-ink">Total credit</td>
                  <td className="px-6 py-3 text-right kc-serif text-lg text-accent">
                    <Money value={cn.total} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </Card>

          {/* Application status */}
          <Card title="Application">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="text-ink-3">
                  <span className="text-ink">Applied {pct}%</span> of total credit
                </div>
                <div className="text-xs text-ink-3">
                  <Money value={cn.appliedTotal} /> applied ·{' '}
                  <Money value={cn.remainingTotal} className="text-accent" /> remaining
                </div>
              </div>
              <div className="h-2 w-full bg-paper-2 rounded">
                <div className="h-2 bg-positive rounded" style={{ width: `${pct}%` }} />
              </div>
              {cn.applications.length === 0 ? (
                <div className="text-xs text-ink-3 italic pt-2">
                  Not yet applied against any invoice.
                </div>
              ) : (
                <ul className="space-y-2 pt-2">
                  {cn.applications.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between text-sm border-b border-rule-2 last:border-0 pb-2 last:pb-0"
                    >
                      <div>
                        <div className="text-ink kc-mono text-xs">{a.invoice.number}</div>
                        <div className="text-[10px] text-ink-3">
                          {new Date(a.appliedAt).toLocaleString('en-MU')} · by {a.appliedBy.name}
                        </div>
                      </div>
                      <Money value={a.amount} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* Journal entry (for accountants) */}
          {cn.journalEntry && (
            <Card title="Journal entry" bodyClassName="p-0">
              <div className="px-6 py-3 border-b border-rule text-xs text-ink-3 flex items-center gap-4">
                <span className="kc-mono text-ink">{cn.journalEntry.number}</span>
                <span>{new Date(cn.journalEntry.date).toLocaleDateString('en-MU')}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Account</th>
                    <th className="px-3 py-2 font-medium text-right">Debit</th>
                    <th className="px-6 py-2 font-medium text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {cn.journalEntry.lines.map((l) => (
                    <tr key={l.id} className="border-b border-rule-2">
                      <td className="px-6 py-2">
                        <span className="kc-mono text-xs text-ink-3">{l.account.code}</span>
                        <span className="ml-2 text-ink-2">{l.account.name}</span>
                        {l.narration && (
                          <div className="text-[10px] text-ink-3 mt-0.5">{l.narration}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right kc-mono">
                        {l.debit !== '0' ? (
                          <Money value={l.debit} />
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                      <td className="px-6 py-2 text-right kc-mono">
                        {l.credit !== '0' ? (
                          <Money value={l.credit} />
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card title="Credit to">
            <div className="text-ink font-medium">{displayName}</div>
            {isCompany && cn.customer.contactPerson && (
              <div className="text-sm text-ink-3 mt-0.5">Attn: {cn.customer.contactPerson}</div>
            )}
            {isCompany && (cn.customer.vatNumber || cn.customer.brn) && (
              <div className="text-xs text-ink-3 mt-1">
                {cn.customer.vatNumber && `VAT ${cn.customer.vatNumber}`}
                {cn.customer.vatNumber && cn.customer.brn && ' · '}
                {cn.customer.brn && `BRN ${cn.customer.brn}`}
              </div>
            )}
            {cn.customer.phone && (
              <div className="text-sm text-ink-2 mt-2">{cn.customer.phone}</div>
            )}
            {cn.customer.email && <div className="text-sm text-ink-3">{cn.customer.email}</div>}
            <Link
              to={`/customers/${cn.customer.id}`}
              className="text-xs text-accent hover:underline block mt-3"
            >
              View customer →
            </Link>
          </Card>

          {cn.invoice && (
            <Card title="Original invoice">
              <div className="text-ink kc-mono text-sm">{cn.invoice.number}</div>
              <div className="text-xs text-ink-3 mt-1">
                Order total: <Money value={cn.invoice.order.total} />
              </div>
              <div className="text-xs text-ink-3">
                Balance due: <Money value={cn.invoice.order.balanceDue} />
              </div>
            </Card>
          )}

          {cn.notes && (
            <Card title="Notes">
              <p className="text-sm text-ink-2 whitespace-pre-wrap">{cn.notes}</p>
            </Card>
          )}
        </div>
      </div>

      {showApply && (
        <ApplyModal
          creditNoteId={cn.id}
          customerId={cn.customer.id}
          defaultInvoiceId={cn.invoice?.id}
          defaultAmount={remainingNum}
          remaining={remainingNum}
          onClose={() => setShowApply(false)}
          onApplied={() => {
            setShowApply(false);
            qc.invalidateQueries({ queryKey: ['credit-note', id] });
            qc.invalidateQueries({ queryKey: ['credit-notes'] });
          }}
        />
      )}

      {showVoid && (
        <VoidModal
          creditNoteId={cn.id}
          onClose={() => setShowVoid(false)}
          onVoided={() => {
            setShowVoid(false);
            qc.invalidateQueries({ queryKey: ['credit-note', id] });
            qc.invalidateQueries({ queryKey: ['credit-notes'] });
          }}
        />
      )}

      {showRefund && (
        <RefundModal
          creditNoteId={cn.id}
          remaining={remainingNum}
          onClose={() => setShowRefund(false)}
          onRefunded={() => {
            setShowRefund(false);
            qc.invalidateQueries({ queryKey: ['credit-note', id] });
            qc.invalidateQueries({ queryKey: ['credit-notes'] });
          }}
        />
      )}
    </PageScaffold>
  );
}

// ─── Apply modal ────────────────────────────────────────────────────────────
interface ApplyableInvoice {
  id: string; // Order.id (used to deep-link)
  number: string; // invoice number
  total: string;
  balanceDue: string;
  invoice: { id: string; number: string } | null;
}

function ApplyModal({
  creditNoteId,
  customerId,
  defaultInvoiceId,
  defaultAmount,
  remaining,
  onClose,
  onApplied,
}: {
  creditNoteId: string;
  customerId: string;
  defaultInvoiceId?: string;
  defaultAmount: number;
  remaining: number;
  onClose: () => void;
  onApplied: () => void;
}): JSX.Element {
  const [invoiceId, setInvoiceId] = useState(defaultInvoiceId ?? '');
  const [amount, setAmount] = useState(String(defaultAmount));
  const [err, setErr] = useState<string | null>(null);

  // Pull the customer's invoiced orders with outstanding balance — those are
  // the natural candidates to absorb a credit. Closed (balance=0) orders are
  // still applicable (would create an overpayment) so we list them too,
  // labelled, so the operator can pick consciously.
  const candidates = useQuery({
    queryKey: ['customer-invoices-for-cn-apply', customerId],
    queryFn: async () => {
      const orders = await apiGet<ApplyableInvoice[]>('/orders', { customerId });
      return (orders ?? [])
        .filter((o) => o.invoice)
        .sort((a, b) => Number(b.balanceDue) - Number(a.balanceDue));
    },
  });

  const selected = (candidates.data ?? []).find((c) => c.invoice?.id === invoiceId);
  const selectableMax = selected
    ? Math.min(remaining, Number(selected.balanceDue) || remaining)
    : remaining;

  // Re-snap the amount when invoice changes — default to "settle this invoice in full" up to remaining.
  const pickInvoice = (inv: ApplyableInvoice): void => {
    if (!inv.invoice) return;
    setInvoiceId(inv.invoice.id);
    const balance = Number(inv.balanceDue);
    const next = balance > 0 ? Math.min(remaining, balance) : remaining;
    setAmount(String(next));
  };

  const apply = useMutation({
    mutationFn: () =>
      apiPost(`/credit-notes/${creditNoteId}/apply`, { invoiceId, amount: Number(amount) }),
    onSuccess: onApplied,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Apply credit to invoice"
      subtitle={`Remaining credit: MUR ${remaining.toLocaleString('en-MU')}`}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              apply.mutate();
            }}
            disabled={!invoiceId || !amount || Number(amount) <= 0 || apply.isPending}
          >
            {apply.isPending ? 'Applying…' : 'Apply credit'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium text-ink-3 uppercase tracking-wide mb-1.5">
            Invoice
          </div>
          {candidates.isLoading ? (
            <div className="text-xs text-ink-3 py-4">Loading customer invoices…</div>
          ) : (candidates.data ?? []).length === 0 ? (
            <div className="text-xs text-ink-3 py-4 italic">
              This customer has no issued invoices.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto border border-rule rounded divide-y divide-rule-2">
              {(candidates.data ?? []).map((inv) => {
                const isOpen = Number(inv.balanceDue) > 0;
                const isSelected = inv.invoice?.id === invoiceId;
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => pickInvoice(inv)}
                    className={cn(
                      'w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition',
                      isSelected ? 'bg-accent-soft' : 'hover:bg-paper-2',
                    )}
                  >
                    <div>
                      <div className="text-sm text-ink kc-mono">{inv.invoice!.number}</div>
                      <div className="text-[10px] text-ink-3 mt-0.5">
                        {isOpen ? (
                          <span className="text-warning">
                            Balance due <Money value={inv.balanceDue} className="text-warning" />
                          </span>
                        ) : (
                          <span className="text-positive">Paid in full</span>
                        )}
                        <span className="text-ink-3">
                          {' '}
                          · of <Money value={inv.total} className="text-ink-3" />
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-ink-3 text-right">
                      <Money value={inv.total} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Input
          label="Amount to apply"
          type="number"
          step="0.01"
          min="0.01"
          max={selectableMax}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={
            selected
              ? `Max for this invoice: MUR ${selectableMax.toLocaleString('en-MU')} (smaller of credit remaining and invoice balance)`
              : `Maximum: MUR ${remaining.toLocaleString('en-MU')}`
          }
        />
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}

// ─── Void modal ─────────────────────────────────────────────────────────────
function VoidModal({
  creditNoteId,
  onClose,
  onVoided,
}: {
  creditNoteId: string;
  onClose: () => void;
  onVoided: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const voidIt = useMutation({
    mutationFn: () => apiPost(`/credit-notes/${creditNoteId}/void`, { reason }),
    onSuccess: onVoided,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Void this credit note?"
      subtitle="Writes a reversing journal entry. The voided CN stays in the books for audit."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              voidIt.mutate();
            }}
            disabled={!reason.trim() || voidIt.isPending}
            className={cn('bg-negative hover:bg-negative/90')}
          >
            {voidIt.isPending ? 'Voiding…' : 'Confirm void'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Issued by mistake — customer never returned the goods"
        />
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}

// ─── Refund modal ───────────────────────────────────────────────────────────
function RefundModal({
  creditNoteId,
  remaining,
  onClose,
  onRefunded,
}: {
  creditNoteId: string;
  remaining: number;
  onClose: () => void;
  onRefunded: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState(String(remaining));
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const amt = Number(amount);
  const methods = ['cash', 'juice', 'emtel', 'myt', 'bank_transfer', 'card', 'cheque'];
  const needsRef = method !== 'cash';

  const refund = useMutation({
    mutationFn: () =>
      apiPost(`/credit-notes/${creditNoteId}/refund`, {
        amount: amt,
        method,
        reference: reference || undefined,
        reason: reason || undefined,
      }),
    onSuccess: onRefunded,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Refund the customer"
      subtitle="Pays the credit back (Dr AR / Cr cash) and draws down this credit note."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              refund.mutate();
            }}
            disabled={
              !(amt > 0) || amt > remaining || (needsRef && !reference.trim()) || refund.isPending
            }
          >
            {refund.isPending ? 'Refunding…' : `Refund Rs ${amt.toLocaleString('en-MU')}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={`Up to Rs ${remaining.toLocaleString('en-MU')} remaining on this credit note.`}
        />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
            Method
          </div>
          <div className="grid grid-cols-3 gap-2">
            {methods.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(m)}
                className={cn(
                  'px-3 py-2 rounded border text-sm transition',
                  method === m
                    ? 'bg-accent text-white border-accent'
                    : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                )}
              >
                {m.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
        {needsRef && (
          <Input
            label="Reference / receipt #"
            required
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="transfer ref, cheque #…"
          />
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
            Reason
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {[
              'Damaged / defective',
              'Wrong price',
              'Goodwill',
              'Overpayment',
              'Order cancelled',
            ].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={cn(
                  'px-2.5 py-1 rounded text-xs border transition',
                  reason === r
                    ? 'bg-accent-soft border-accent text-accent'
                    : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                )}
              >
                {r}
              </button>
            ))}
          </div>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for the refund (pick above or type your own)"
          />
        </div>
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}
